import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import reduxUtils from 'util/redux';
import classnames from 'classnames';
import looppa from 'looppa';
import { NavLink } from 'react-router-dom';
import { t, Trans } from 'translations';
import Icon from 'components/icon';
import Loader from 'components/loader';
import categoryIcons from 'constants/category-icons';
import { linkToDashboardByRoute } from 'util/misc/link-to-dashboard';
import normaliseTrailingSlash from 'util/misc/normalise-trailing-slash';
import { breadcrumbsDelay, breadcrumbsEasingFn } from 'constants/transitions-props';
import Animation from 'components/animations';

const PLACEHOLDER_ICON = categoryIcons.default;

/**
 * @param {ICategory} category
 */
function isRootCategory(category) {
    return category.level < 1 || category.is_direct_link;
}

/**
 * @param {ICategory} category
 */
function isSubCategory(category) {
    return category.level >= 1 && !isLeafCategory(category);
}

/**
 * @param {ICategory} category
 */
function isLeafCategory(category) {
    return category.level === 2;
}

/**
 * @param {ICategory} category
 */
function hasLeafChildCategories(category) {
    return category.children.length && isLeafCategory(category.children[0]);
}

function Title({ isFixed, category, pathname, routerCooperative }) {
    const overviewLink = `${ linkToDashboardByRoute(routerCooperative) }${ category.slug }`;

    if (hasLeafChildCategories(category)) {
        return (
            <h2 className={ classnames('categories-menu--title', { 'categories-menu--title__fixed': isFixed }) }>
                <span className="categories-menu--title-text">{ category.name }</span>
            </h2>
        );
    }

    return (
        <h2 className="categories-menu--title">
            <Icon
                className="categories-menu--category-icon"
                symbol={ categoryIcons[category.id] || PLACEHOLDER_ICON }
                size="large"/>
            <div className="categories-menu--title-content">
                <span className="categories-menu--title-text">{ category.name }</span>
                { normaliseTrailingSlash(pathname) !== normaliseTrailingSlash(overviewLink) &&
                <NavLink
                    className="categories-menu--show-all u-reset"
                    to={ normaliseTrailingSlash(overviewLink) }>
                    <Trans path={ 'categories.show_all' }/>
                </NavLink>
                }
            </div>
        </h2>
    );
}

Title.propTypes = {
    category: PropTypes.object.isRequired,
    isFixed: PropTypes.bool,
    pathname: PropTypes.string,
    routerCooperative: PropTypes.string,
};

class CategoriesMenu extends PureComponent {
    static propTypes = {
        isCategorylistVisible: PropTypes.bool.isRequired,
        categories: PropTypes.array.isRequired,
        activeCategory: PropTypes.object,
        fetchingCategory: PropTypes.object,
        activeLeaf: PropTypes.object,
        isFetching: PropTypes.bool.isRequired,
        setActiveCategory: PropTypes.func.isRequired,
        fetchCategories: PropTypes.func.isRequired,
        setPendingAutoscroll: PropTypes.func.isRequired,
        setActiveLeafCategory: PropTypes.func.isRequired,
        routerRegistry: PropTypes.object.isRequired,
        canUpdateBreadcrumbs: PropTypes.bool.isRequired,
        resetBreadcrumbs: PropTypes.func.isRequired,
        routerCooperative: PropTypes.string.isRequired,
        history: PropTypes.object.isRequired,
        isMobile: PropTypes.bool.isRequired,
        setBreadcrumbs: PropTypes.func,
        routeName: PropTypes.string,
        categoriesCooperative: PropTypes.string,
        // this property is here just because redux needs to re render this component
        // in case of url updates
        // see also: https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/guides/blocked-updates.md
        pathname: PropTypes.string.isRequired,
    };

    static defaultProps = {
        canUpdateBreadcrumbs: true,
    };

    state = {
        isMobileMenuOpen: false,
        currentActiveCategory: null,
        currentActiveCategories: [],
        animationDirection: 'drill-down',
    };

    componentWillMount() {
        this.props.fetchCategories().then(() => {
            this.setCurrentActiveCategory(this.props.activeCategory);
        });

        this.setState({ isMobileMenuOpen: this.props.isCategorylistVisible });
    }

    componentWillReceiveProps({ activeCategory }) {
        if (activeCategory !== this.props.activeCategory) {
            window.scrollTo(0, 0);
            this.setCurrentActiveCategory(activeCategory);
        }
    }

    componentWillUpdate(nextProps) {
        this.setAnimationDirection(nextProps);

        const { isCategorylistVisible } = nextProps;
        if (isCategorylistVisible !== this.props.isCategorylistVisible) {
            this.setState({ isMobileMenuOpen: isCategorylistVisible });
        }
    }

    componentWillUnmount() {
        this.setCurrentActiveCategory.cancel();
    }

    onLinkClick(category, event) {
        switch (true) {
            // just simple menu link
            case category.is_direct_link:
                this.props.history.push(category.slug);
                break;
            case isSubCategory(category):
                this.fetchSubcategories(event, category);
                break;

            case isRootCategory(category):
                event.preventDefault();
                this.updateBreadcrumbs();
                this.props.setActiveCategory({ category });
                break;
            default:
                if (!this.props.activeLeaf || this.props.activeLeaf.id !== category.id) {
                    this.props.setPendingAutoscroll({ mustAutoscroll: true });
                    this.props.setActiveLeafCategory({ category });
                }
        }
    }

    /**
     * Fetch all the subcategories of the active main category and browse to its detail page
     * @param {ICategory} category - the current category
     */
    fetchSubcategories = (event, category) => {
        if (!category.children.length) {
            event.preventDefault();
            const onDone = () => this.props.history.push(normaliseTrailingSlash(this.getCategorySlug(category)));
            this.props.fetchCategories({ category })
                .then(onDone)
                .catch(onDone);
        }
    };

    updateBreadcrumbs = () => {
        if (!this.props.canUpdateBreadcrumbs) {
            return;
        }

        this.props.setBreadcrumbs({
            breadcrumbs: [
                {
                    name: t('breadcrumbs.overview'),
                    action: () => {
                        this.props.setActiveCategory({ category: null });
                        this.props.resetBreadcrumbs();
                    },
                },
            ],
        });
    };

    setCurrentActiveCategory = debounce(function(activeCategory) {
        const newState = {
            currentActiveCategory: activeCategory,
            currentActiveCategories: activeCategory ? activeCategory.children : this.props.categories,
        };
        this.setAnimationDirection(this.props);
        this.setState(newState);
    }, breadcrumbsDelay);

    /**
     * @param {ICategory} category
     * @return {*}
     */
    getCategorySlug(category) {
        const { routerCooperative } = this.props;

        if (isSubCategory(category) && category.children.length) {
            return linkToDashboardByRoute(routerCooperative) + category.children[0].slug;
        }

        return linkToDashboardByRoute(routerCooperative) + category.slug;
    }

    /**
     * Get name for animation name based on the navigation hierarchy
     * @param {ICategory | null} activeCategory
     * @param {ICategory | null} oldActiveCategory
     * @return {string}
     */
    getDirectionName = (activeCategory, oldActiveCategory) => {
        if (activeCategory && oldActiveCategory && oldActiveCategory.level < activeCategory.level) {
            return 'drill-up';
        }
        return 'drill-down';
    };

    /**
     * @param {ICategory} activeCategory
     */
    setAnimationDirection = ({ activeCategory }) => {
        const oldActiveCategory = this.props.activeCategory;
        const animationDirection = this.getDirectionName(activeCategory, oldActiveCategory);

        if (animationDirection !== this.state.animationDirection) {
            this.setState({ animationDirection });
        }
    };

    /**
     * Remap the animation direction to transition names
     * @returns {string}
     */
    getAnimationName() {
        const { isMobile, isCategorylistVisible } = this.props;
        const { isMobileMenuOpen } = this.state;
        let animationName = this.state.animationDirection === 'drill-down' ? 'slideRight' : 'slideLeft';

        if (isMobile) {
            // only show the slidedown animation if we are on mobile and the menu changes is state between close and open (vice versa)
            if (isMobileMenuOpen !== isCategorylistVisible) {
                animationName = 'slideDown';
            }
        }

        return animationName;
    }

    renderIcon(category) {
        if (this.props.isFetching && this.props.fetchingCategory && this.props.fetchingCategory.id === category.id) {
            return <Loader facet={ 'small' }/>;
        }

        return isLeafCategory(category) ? null : <Icon symbol="arrow-right" size="small"/>;
    }

    renderLink(category) {
        const icon = this.renderIcon(category);
        return (
            <NavLink
                className="categories-menu--link u-reset"
                exact
                onClick={ (e) => this.onLinkClick(category, e) }
                activeClassName={ isLeafCategory(category) ? 'categories-menu--link__active' : '' }
                to={ normaliseTrailingSlash(this.getCategorySlug(category)) }>
                { isRootCategory(category) ?
                    <Icon
                        className="categories-menu--category-icon"
                        symbol={ categoryIcons[category.id] || PLACEHOLDER_ICON }
                        size="large"/> : ''
                }
                <span className="categories-menu--text">{ category.name }</span>
                { icon && <span className="categories-menu--icon">{ icon }</span> }
            </NavLink>
        );
    }

    renderMenuContent() {
        const { pathname, categories, routerCooperative } = this.props;
        const { currentActiveCategories, currentActiveCategory } = this.state;

        if (!categories.length) {
            return <Loader/>;
        }

        return (
            <div key={ currentActiveCategories }>
                { currentActiveCategory ?
                    <Title
                        pathname={ pathname }
                        routerCooperative={ routerCooperative }
                        category={ currentActiveCategory }/> : ''
                }
                <ul className="u-reset categories-menu--list">
                    { looppa(currentActiveCategories)((category) => (
                        <li key={ category.id } className="categories-menu--item">
                            { this.renderLink(category) }
                        </li>
                    )) }
                </ul>
            </div>
        );
    }

    render() {
        const { isCategorylistVisible, isMobile } = this.props;

        return (
            <div className="categories-menu grid">
                <div className={ 'grid--container' }>
                    <Animation
                        animation={ this.getAnimationName() }
                        duration={ 200 }
                        easing={ breadcrumbsEasingFn }
                        aria-label="Side Menu"
                    >
                        { (isCategorylistVisible || !isMobile) && this.renderMenuContent() }
                    </Animation>
                </div>
            </div>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        isCategorylistVisible: 'categories.isCategorylistVisible',
        categories: 'categories.items',
        fetchingCategory: 'categories.fetchingCategory',
        activeCategory: 'categories.active',
        routerCooperative: 'router.cooperative',
        isFetching: 'categories.isFetching',
        pathname: 'router.location.pathname',
        routerRegistry: 'router.registry',
        history: 'router.history',
        routeName: 'router.name',
        isMobile: 'viewport.isMobile',
    }),
    reduxUtils.mapDispatchToProps({
        setActiveCategory: 'categories.setActiveCategory',
        fetchCategories: 'categories.fetchCategories',
        setBreadcrumbs: 'breadcrumbs.set',
        resetBreadcrumbs: 'breadcrumbs.reset',
        setActiveLeafCategory: 'categories.setActiveLeafCategory',
        setPendingAutoscroll: 'scroll.setPendingAutoscroll',
    }),
)(CategoriesMenu);




// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/categories-menu/index.js